import type { SmsRepository } from '../application/SmsRepository'
import {
  canLiftBlock,
  consentRowSchema,
  consentStateOf,
  isBlocked,
  proofOccurredAt,
  torontoToday,
  type ConsentDetail,
  type ConsentRow,
} from '../domain/smsConsents'
import { journalRowSchema, type JournalEntry } from '../domain/smsJournal'
import { SmsCommandError, type ConfiguredSmsSettings, type ProviderCheck } from '../domain/smsSettings'

/**
 * Données locales (mode `fixtures`, développement sans Supabase) : simulation, jamais vérifiée. Aucun SMS, aucun
 * appel Telnyx. Le diagnostic simulé le dit explicitement. Journal et consentements partent vides : seules les
 * actions faites dans cette session locale y apparaissent, jamais des données de démonstration.
 */
export function createFixtureSmsRepository(): SmsRepository {
  let settings: ConfiguredSmsSettings = {
    configured: true,
    canManage: true,
    mode: 'SIMULATION',
    senderE164: '+14509907539',
    messagingProfileId: null,
    countries: ['CA'],
    sendingPaused: false,
    webhookLastReceivedAt: null,
    providerCheckedAt: null,
    providerCheck: null,
    segmentPriceKnown: false,
    updatedAt: new Date().toISOString(),
    dailyVolumeCap: null,
    outboundToday: 0,
    testAllowlist: [],
  }
  const journal: JournalEntry[] = []
  const contacts = new Map<string, ConsentDetail>()

  function normalize(phone: string): string {
    const digits = phone.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '')
    if (digits.length !== 10) throw new SmsCommandError('SMS_INVALID_NUMBER')
    return `+1${digits}`
  }

  function contact(contactId: string): ConsentDetail {
    const found = contacts.get(contactId)
    if (!found) throw new SmsCommandError('SMS_UNKNOWN_CONTACT')
    return found
  }

  function conversationNotFound(): Promise<never> {
    return Promise.reject(new SmsCommandError('SMS_CONVERSATION_NOT_FOUND'))
  }

  function toRow(detail: ConsentDetail): ConsentRow {
    // Le schéma de ligne ne garde que les champs de la liste.
    return consentRowSchema.parse(detail)
  }

  return {
    async getSettingsOverview() {
      return Promise.resolve(settings)
    },
    async checkProvider() {
      const check: ProviderCheck = {
        checkedAt: new Date().toISOString(),
        apiKeyConfigured: false,
        publicKeyConfigured: false,
        apiReachable: false,
        profileFound: false,
        profileEnabled: false,
        webhookUrlMatches: false,
        destinations: [],
        numberAttached: false,
        smsTwoWay: false,
        numberType: null,
        dailySpendLimit: null,
        problems: ['Données locales : aucune lecture Telnyx.'],
      }
      settings = { ...settings, providerCheck: check, providerCheckedAt: check.checkedAt }
      return Promise.resolve(check)
    },
    async setSendingPaused(paused) {
      settings = { ...settings, sendingPaused: paused }
      return Promise.resolve(settings)
    },
    async requestTestSms(to) {
      const now = new Date().toISOString()
      journal.unshift({
        id: crypto.randomUUID(),
        state: 'BLOCKED',
        outboxState: 'BLOCKED',
        reason: 'NOT_IN_TEST_ALLOWLIST',
        sourceModule: 'SMS',
        scope: 'SNOW_ALERTS',
        phone: normalize(to),
        clientName: null,
        templateName: 'Paramètres : SMS test',
        templateKey: 'parametres.test',
        templateVersion: 1,
        authorName: 'Session locale',
        body: 'Groupe RECA : message test du module Alertes SMS. Aucune action requise. STOP pour vous desinscrire.',
        createdAt: now,
        updatedAt: now,
        notBefore: now,
        expiresAt: now,
        attempts: 0,
        senderE164: settings.senderE164,
        message: null,
        events: [],
      })
      return Promise.resolve({ state: 'BLOCKED', reason: 'NOT_IN_TEST_ALLOWLIST' })
    },
    async listJournal(filter) {
      const rows = journal
        .filter((entry) => !filter.state || entry.state === filter.state)
        .filter((entry) => !filter.module || entry.sourceModule === filter.module)
        .filter((entry) => !filter.failuresOnly || entry.state === 'SEND_FAILED')
        .map((entry) =>
          journalRowSchema.parse({
            ...entry,
            providerStatus: entry.message?.providerStatus ?? null,
            errorCode: entry.message?.errorCode ?? null,
          }),
        )
      return Promise.resolve({ rows, hasMore: false })
    },
    async getJournalEntry(id) {
      const entry = journal.find((candidate) => candidate.id === id)
      if (!entry) throw new SmsCommandError('SMS_NOT_FOUND')
      return Promise.resolve(entry)
    },
    async listConsents(filter) {
      const search = filter.search?.replace(/\D/g, '') ?? ''
      const rows = [...contacts.values()]
        .filter((detail) => !search || detail.phone.includes(search))
        .filter((detail) => {
          if (!filter.state) return true
          if (filter.state === 'BLOCKED') return isBlocked(detail)
          const states = (filter.scope ? [filter.scope] : (['SNOW_ALERTS', 'ADMINISTRATIVE'] as const)).map(
            (scope) => consentStateOf(detail, scope),
          )
          return filter.state === 'NONE'
            ? states.every((state) => state === null)
            : states.includes(filter.state)
        })
        .map(toRow)
      return Promise.resolve({ rows, total: rows.length })
    },
    async getConsentDetail(contactId) {
      return Promise.resolve(contact(contactId))
    },
    // Démonstration : aucun client réel, rien n'est envoyé.
    getConsentCollectionPreview() {
      return Promise.resolve({
        clients: 0,
        blocked: 0,
        alertsGranted: 0,
        adminGranted: 0,
        withoutEmail: 0,
        emailEligible: 0,
        smsEligible: 0,
        smsMode: 'DISABLED' as const,
        requestWording: '',
      })
    },
    sendConsentInvitations() {
      return Promise.resolve({ sent: 0, failed: 0, alreadySent: 0 })
    },
    sendConsentRequests() {
      return Promise.resolve({ queued: 0, replayed: 0, blocked: {} })
    },
    async recordConsentProof(command) {
      const phone = normalize(command.phone)
      const now = new Date().toISOString()
      const existing = [...contacts.values()].find((detail) => detail.phone === phone)
      const base: ConsentDetail = existing ?? {
        contactId: crypto.randomUUID(),
        phone,
        clients: [],
        snowAlerts: null,
        administrative: null,
        openRequest: null,
        localBlockedAt: null,
        localBlockReason: null,
        providerOptedOutAt: null,
        providerOptOutType: null,
        lastEvent: null,
        updatedAt: now,
        proofs: [],
        events: [],
      }
      const at = proofOccurredAt(command.occurredOn, torontoToday()) ?? now
      const proof = {
        scope: command.scope,
        source: 'OFFLINE_PROOF',
        wordingText: command.wordingText,
        wordingVersion: command.wordingVersion,
        proofNote: command.proofNote,
        actorName: 'Session locale',
        at,
      }
      const updated: ConsentDetail = {
        ...base,
        [command.scope === 'SNOW_ALERTS' ? 'snowAlerts' : 'administrative']: 'GRANTED',
        lastEvent: { kind: 'GRANTED', scope: command.scope, source: 'OFFLINE_PROOF', at },
        proofs: [...base.proofs.filter((candidate) => candidate.scope !== command.scope), proof],
        events: [{ id: crypto.randomUUID(), kind: 'GRANTED', ...proof }, ...base.events],
      }
      contacts.set(updated.contactId, updated)
      return Promise.resolve(updated)
    },
    async withdrawConsent(command) {
      if (!command.note.trim()) throw new SmsCommandError('SMS_NOTE_REQUIRED')
      if (command.scopes.length === 0 && !command.blockAll)
        throw new SmsCommandError('SMS_NOTHING_TO_WITHDRAW')
      let detail = contact(command.contactId)
      const now = new Date().toISOString()
      for (const scope of command.scopes) {
        const state = consentStateOf(detail, scope)
        if (state !== 'GRANTED' && state !== 'PENDING') throw new SmsCommandError('SMS_SCOPE_NOT_ACTIVE')
        detail = {
          ...detail,
          [scope === 'SNOW_ALERTS' ? 'snowAlerts' : 'administrative']: 'REVOKED',
          events: [
            {
              id: crypto.randomUUID(),
              kind: 'REVOKED',
              scope,
              source: 'AGENT',
              wordingVersion: null,
              proofNote: command.note,
              actorName: 'Session locale',
              at: now,
            },
            ...detail.events,
          ],
        }
      }
      if (command.blockAll) {
        detail = {
          ...detail,
          localBlockedAt: detail.localBlockedAt ?? now,
          localBlockReason: detail.localBlockReason ?? 'AGENT_REQUEST',
          events: [
            {
              id: crypto.randomUUID(),
              kind: 'GLOBAL_BLOCK',
              scope: null,
              source: 'AGENT',
              wordingVersion: null,
              proofNote: command.note,
              actorName: 'Session locale',
              at: now,
            },
            ...detail.events,
          ],
        }
      }
      detail = {
        ...detail,
        lastEvent: detail.events[0]
          ? { kind: detail.events[0].kind, scope: detail.events[0].scope, source: 'AGENT', at: now }
          : null,
      }
      contacts.set(detail.contactId, detail)
      return Promise.resolve(detail)
    },
    async liftBlock(command) {
      if (!command.note.trim()) throw new SmsCommandError('SMS_NOTE_REQUIRED')
      const detail = contact(command.contactId)
      if (!canLiftBlock(detail)) throw new SmsCommandError('SMS_BLOCK_NOT_LIFTABLE')
      const now = new Date().toISOString()
      const updated: ConsentDetail = {
        ...detail,
        localBlockedAt: null,
        localBlockReason: null,
        lastEvent: { kind: 'GLOBAL_UNBLOCK', scope: null, source: 'AGENT', at: now },
        events: [
          {
            id: crypto.randomUUID(),
            kind: 'GLOBAL_UNBLOCK',
            scope: null,
            source: 'AGENT',
            wordingVersion: null,
            proofNote: command.note,
            actorName: 'Session locale',
            at: now,
          },
          ...detail.events,
        ],
      }
      contacts.set(updated.contactId, updated)
      return Promise.resolve(updated)
    },
    // Boîte de réception locale : aucun SMS entrant sans Telnyx, donc aucune conversation inventée.
    async listInbox() {
      return Promise.resolve({ rows: [], counts: { TO_DO: 0, UNREAD: 0, MINE: 0, ALL: 0 } })
    },
    async getInboxUnreadCount() {
      return Promise.resolve(0)
    },
    getConversation() {
      return conversationNotFound()
    },
    markConversationRead() {
      return conversationNotFound()
    },
    setConversationStatus() {
      return conversationNotFound()
    },
    assignConversation() {
      return conversationNotFound()
    },
    addConversationNote() {
      return conversationNotFound()
    },
    reply() {
      return conversationNotFound()
    },
    async searchLinkCandidates() {
      return Promise.resolve([])
    },
    linkContactClient() {
      return conversationNotFound()
    },
    // Modèles locaux : aucune bibliothèque sans Supabase (les brouillons vivent en base, inactifs).
    async listTemplates() {
      return Promise.resolve([])
    },
    async getTemplateVersions() {
      return Promise.resolve([])
    },
    saveTemplateVersion() {
      return Promise.reject(new SmsCommandError('SMS_TEMPLATE_NOT_FOUND'))
    },
    setTemplateActive() {
      return Promise.reject(new SmsCommandError('SMS_TEMPLATE_NOT_FOUND'))
    },
    async listReplyTemplates() {
      return Promise.resolve([])
    },
    // Envois locaux : aucune route ni modèle sans Supabase, donc aucun envoi possible.
    async getCampaignOptions() {
      return Promise.resolve({ seasons: [], season: null, routes: [], templates: [], inactiveTemplates: [] })
    },
    previewCampaign() {
      return Promise.reject(new SmsCommandError('SMS_CAMPAIGN_TEMPLATE_INVALID'))
    },
    createCampaign() {
      return Promise.reject(new SmsCommandError('SMS_CAMPAIGN_TEMPLATE_INVALID'))
    },
    cancelCampaign() {
      return Promise.reject(new SmsCommandError('SMS_CAMPAIGN_NOT_FOUND'))
    },
    async listCampaigns() {
      return Promise.resolve([])
    },
    getCampaign() {
      return Promise.reject(new SmsCommandError('SMS_CAMPAIGN_NOT_FOUND'))
    },
    // Aucun contrat local : seul l'état du module est réel ici.
    async getContractSmsOverview() {
      return Promise.resolve({
        module: { mode: settings.mode, sendingPaused: settings.sendingPaused },
        phones: [],
        consentsVisible: true,
        routes: [],
        campaigns: [],
        messages: [],
      })
    },
    async getClientSmsAlertStatus(clientIds) {
      // Démonstration : un client sur deux reçoit les alertes.
      return Promise.resolve(clientIds.map((clientId, index) => ({ clientId, active: index % 2 === 0 })))
    },
  }
}
